import { FC, isValidElement } from 'react';

import { getErrorMessagesIds } from '#/components/Form/Form.utils';
import { horizontalDirection } from '#/service/constants';

import {
  Container,
  InputContainer,
  InputLabel,
  StyledIcon,
  StyledInput,
} from '../../FormInput.styled';
import { FormInputDetails } from '../FormInputDetails';
import { useSplitTimestamp } from './SplitTimestamp.hook';
import { SplitTimestampProps } from './SplitTimestamp.types';

export const SplitTimestamp: FC<SplitTimestampProps> = ({
  dataKey,
  description,
  errorMessages,
  inputValue,
  label,
  required,
  help,
  groupKey = '',
  inputType,
  touched,
  ...rest
}) => {
  const {
    props,
    handleFocusConditionally,
    handleFocusWithRef,
    handleChange,
    blurInput,
    overrideDataTestId,
    dateRange,
  } = useSplitTimestamp({
    inputType,
    errorMessages,
    dataKey,
    ...rest,
  });

  return (
    <Container>
      <InputContainer
        className={inputValue && 'filled'}
        data-testid="input-container"
        onClick={handleFocusConditionally}
      >
        <InputLabel $bigLabel={false} htmlFor={`${groupKey}-${dataKey}`}>
          {label}
        </InputLabel>
        <StyledInput
          $hasRightIcon={isValidElement(help)}
          $touched={touched}
          aria-describedby={
            errorMessages?.length
              ? getErrorMessagesIds({
                  groupKey,
                  dataKey,
                  errorMessages,
                })
              : undefined
          }
          aria-invalid={!!errorMessages?.length}
          data-testid={overrideDataTestId ?? 'styled-input'}
          id={`${groupKey}-${dataKey}`}
          max={dateRange.max}
          min={dateRange.min}
          name={dataKey}
          onBlur={blurInput}
          onChange={handleChange}
          onFocus={handleFocusWithRef}
          required={required}
          type={inputType}
          value={inputValue ?? ''}
          {...props}
        />
        {help && (
          <>
            <StyledIcon
              aria-hidden="true"
              data-for={`help-${dataKey}`}
              data-tip
              name="questionmark_circle"
              position={horizontalDirection.RIGHT}
            />
            {help}
          </>
        )}
      </InputContainer>
      <FormInputDetails
        dataKey={dataKey}
        description={description}
        errorMessages={errorMessages}
        groupKey={groupKey}
      />
    </Container>
  );
};
